import React, { useRef, useState } from 'react';
import SPADataTable from '@/components/admin/SysadminSPA/shared/SPADataTable';
import { useSPAToast } from '@/components/admin/SysadminSPA/shared/SPAToast';
import NoteForm from './NoteForm';
import noteColumns from './noteColumns';
import {
  NoteValues, SlideStatus, useSlideStatusMutations, useSlideStatuses,
} from './useSlideStatuses';

interface SlideAdminNotesPanelProps {
  initialNotes?: SlideStatus[];
  slideId: number;
  stafferId: number;
  statuses: string[];
}

// The add form, the note list with in-row editing, and the mutations behind them. The Edit
// Presentation dialog and the builder's Admin Notes drawer both render this.
const SlideAdminNotesPanel = ({
  initialNotes, slideId, stafferId, statuses,
}: SlideAdminNotesPanelProps) => {
  const { data: notes = [] } = useSlideStatuses(slideId, initialNotes);
  const {
    create, destroy, setDone, update,
  } = useSlideStatusMutations(slideId);
  const [editingId, setEditingId] = useState<number | undefined>();
  const draft = useRef<NoteValues>({ note: '', status: '' });
  const [addedCount, setAddedCount] = useState(0);
  const spaToast = useSPAToast();

  const handleAdd = (values: NoteValues) => create.mutate(values, {
    onSuccess: () => { setAddedCount(count => count + 1); spaToast.success('Note added.'); },
  });

  const handleSaveEdit = () => {
    if (editingId === undefined || update.isLoading) return;

    update.mutate({ id: editingId, note: draft.current.note.trim(), status: draft.current.status }, {
      onSuccess: () => { setEditingId(undefined); spaToast.success('Note updated.'); },
    });
  };

  const startEdit = (note: SlideStatus) => {
    draft.current = { note: note.note ?? '', status: note.status };
    setEditingId(note.id);
  };

  const columns = noteColumns({
    draft,
    editingId,
    isDeleting: destroy.isLoading,
    isMarking: setDone.isLoading,
    isSaving: update.isLoading,
    onCancelEdit: () => setEditingId(undefined),
    onDelete: id => destroy.mutateAsync(id).then(() => spaToast.success('Note deleted.')),
    onEdit: startEdit,
    onSaveEdit: handleSaveEdit,
    onSetDone: (note: SlideStatus, done: boolean) => setDone.mutate({ done, id: note.id }, {
      onSuccess: () => spaToast.success(done ? 'Marked done.' : 'Reopened.'),
    }),
    stafferId,
    statuses,
  });

  const hasError = create.isError || update.isError || destroy.isError || setDone.isError;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <NoteForm key={addedCount} isSaving={create.isLoading} statuses={statuses} onSubmit={handleAdd} />
      {hasError && (
        <p className="tw-m-0 tw-text-sm tw-text-spa-error" role="alert">
          The note could not be saved. Try again.
        </p>
      )}
      <SPADataTable
        columns={columns}
        data={notes}
        defaultSort={[{ desc: true, id: 'created_at' }]}
        emptyMessage="No admin notes on this slide yet."
        footnote="Times are in your time zone"
        pagination={{ defaultPageSize: 10 }}
      />
    </div>
  );
};

export default SlideAdminNotesPanel;
